<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { FieldSearchBar, ConfirmDeleteModal } from '@/shared/ui';
  import type { MemberSearchField } from '@/shared/lib';
  import PasswordDisplayModal from '../PasswordDisplayModal.vue';
  import { UsersTable, CreateUserModal, EditUserModal } from '../../ui';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import {
    useAdminListPageSetup,
    createAdminListPageProps,
    useUserManagement,
  } from '../../lib';
  import { searchAdminUsers } from '../../api';
  import { userSearchFieldOptions } from '../../model';
  import type { AdminUser } from '../../model';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { useUserStore } from '@/entities/User';
  import UserFilterModal from './UserFilterModal.vue';
  import { useAdminUsersExport } from './useAdminUsersExport';

  const props = defineProps(createAdminListPageProps());

  const userStore = useUserStore();

  const searchFieldId = ref<MemberSearchField>('name');

  const {
    items: users,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    cleanup,
    handleFilter,
    handleSortOptions,
    isUsingFilters,
    sort,
    order,
    filter,
    handleTableFilter,
  } = useAdminListPageSetup<AdminUser>(
    {
      defaultSearchUrl: '/users',
      searchFunction: searchAdminUsers,
      defaultAmount: 10,
      searchFieldRef: searchFieldId,
    },
    props
  );

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    UserFilterModal,
    { clearFilters: () => {}, filters: {} },
    handleFilter,
    () => filter.value
  );

  const createUserModal = useModal({
    component: CreateUserModal,
    attrs: {
      title: 'Novo Usuário',
      get showSuperadminToggle() {
        return userStore.isSuperuser;
      },
      onClose() {
        createUserModal.close();
      },
      async onSave() {
        createUserModal.close();
        await initSearch();
      },
    },
  });

  const handleCreateUser = () => {
    createUserModal.open();
  };

  const { resetPassword } = useUserManagement();
  const resetUserId = ref<number | null>(null);
  const resetPasswordValue = ref('');

  const confirmResetModal = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: 'Deseja redefinir a senha deste usuário?',
      description: 'Uma nova senha será gerada automaticamente.',
      onClose() {
        confirmResetModal.close();
      },
      async onConfirm() {
        confirmResetModal.close();
        if (resetUserId.value) {
          const result = await resetPassword(resetUserId.value);
          if (result.success && result.generatedPassword) {
            resetPasswordValue.value = result.generatedPassword;
            resetPasswordModal.open();
          }
        }
      },
    },
  });

  const resetPasswordModal = useModal({
    component: PasswordDisplayModal,
    attrs: {
      get password() { return resetPasswordValue.value; },
      onClose() {
        resetPasswordModal.close();
        resetPasswordValue.value = '';
        resetUserId.value = null;
      },
    },
  });

  const handleResetPassword = (id: number) => {
    resetUserId.value = id;
    confirmResetModal.open();
  };

  const { handleExportToExcel } = useAdminUsersExport({
    searchTerm,
    sort,
    order,
    filter,
    searchFieldId,
  });

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const handleDelete = async (_deletedIds: number[]) => {
    await initSearch();
  };

  const editUserId = ref<number | null>(null);

  const editUserModal = useModal({
    component: EditUserModal,
    attrs: {
      get userId() { return editUserId.value!; },
      get showSuperadminToggle() {
        return userStore.isSuperuser;
      },
      onClose() {
        editUserModal.close();
        editUserId.value = null;
      },
      async onSave() {
        editUserModal.close();
        editUserId.value = null;
        await initSearch();
      },
    },
  });

  const handleEdit = (id: number) => {
    editUserId.value = null;
    editUserId.value = id;
    editUserModal.open();
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });
</script>

<template>
  <AdminListPageLayout
    data-testid="admin-users-page"
    title="Usuários"
    create-button-text="Novo Usuário"
    variant="stacked"
    entity-name="usuário"
    empty-state-message="Nenhum usuário cadastrado"
    :items="users"
    :loading="loading"
    :search-term="searchTerm"
    :metadata="metadata"
    :selected-quantity="selectedQuantity"
    :is-using-filters="isUsingFilters"
    :current-page="currentPage"
    @search="handleSearch"
    @create="handleCreateUser"
    @export="handleExportToExcel"
    @page-changed="onPageChanged"
    @update:selected-quantity="handleQuantityChange"
  >
    <template #search="{ onSearch }">
      <FieldSearchBar
        v-model:search-field-id="searchFieldId"
        independent-search-field
        :search-fields="userSearchFieldOptions"
        :initial-search-term="searchTerm"
        class="flex-1"
        @search="onSearch"
      />
    </template>

    <template #table>
      <UsersTable
        :users="users"
        :is-filter-active="isFilterActive"
        :search-term="searchTerm"
        class="mt-6"
        @update:sort="handleSortOptions"
        @update:filter="handleTableFilter"
        @filter-clicked="handleFilterClicked"
        @delete-completed="handleDelete"
        @edit="handleEdit"
        @reset-password="handleResetPassword"
      />
    </template>
  </AdminListPageLayout>
</template>
